import { ExtrudeGeometry, MathUtils, Shape, Vector3 } from 'three';

export type BeveledPlateProps = {
  size: Vector3;
  cornerRadius: number;
  edgeRadius: number;
  topOffset?: number;
  edgeAngle?: number;
};

export default class BeveledPlate {
  private size: Vector3;
  private readonly cornerRadius: number;
  private readonly edgeRadius: number;
  private readonly topOffset: number;
  private readonly edgeAngle: number | undefined;
  private readonly edgeAngled: boolean;

  constructor({
    size = new Vector3(),
    cornerRadius = 0.1,
    edgeRadius = 0.1,
    topOffset = 0,
    edgeAngle,
  }: BeveledPlateProps) {
    this.size = size;
    this.cornerRadius = cornerRadius;
    this.edgeRadius = edgeRadius;
    this.topOffset = topOffset;
    this.edgeAngle = edgeAngle;
    this.edgeAngled = !!this.edgeAngle;
  }

  public getGeometry(): ExtrudeGeometry {
    const profileShape = this.getProfileShape(this.edgeRadius);

    const geometry = new ExtrudeGeometry(profileShape, {
      steps: 32,
      depth: this.size.y - 2 * this.edgeRadius,
      bevelEnabled: true,
      bevelThickness: this.edgeRadius,
      bevelSize: this.edgeRadius,
      bevelOffset: 0,
      bevelSegments: 32,
    });

    this.updateInternals(geometry);

    if (this.edgeAngled) {
      this.applyBevelToGeometry(geometry);
    }
    this.updateInternals(geometry);
    //this.compileGroups(geometry);
    this.updateInternals(geometry);
    geometry.center();

    return geometry;
  }

  private updateInternals(geometry: ExtrudeGeometry): void {
    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.normal.needsUpdate = true;
    geometry.attributes.uv.needsUpdate = true;
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
  }

  private getProfileShape(offset = 0): Shape {
    const shape = new Shape();

    const radius = this.cornerRadius;
    const length = (this.size.x - offset) / 2;
    const width = (this.size.z - offset) / 2;

    shape.moveTo(length, 0);
    shape.lineTo(length, width - radius);
    shape.absarc(
      length - radius,
      width - radius,
      radius,
      0,
      Math.PI / 2,
      false
    );
    shape.absarc(
      -length + radius,
      width - radius,
      radius,
      Math.PI / 2,
      Math.PI,
      false
    );
    shape.absarc(
      -length + radius,
      -width + radius,
      radius,
      -Math.PI,
      -Math.PI / 2,
      false
    );
    shape.absarc(
      length - radius,
      -width + radius,
      radius,
      -Math.PI / 2,
      0,
      false
    );
    shape.lineTo(length, 0);

    return shape;
  }

  private applyBevelToGeometry(geometry: ExtrudeGeometry) {
    const cutStart = (geometry.boundingBox?.min.z ?? 0) + this.topOffset;
    const position = geometry.attributes.position;

    for (let i = 0; i < position.count; i++) {
      let x = position.getX(i);
      let y = position.getY(i);
      const z = position.getZ(i);

      if (z >= cutStart) {
        const curHeight = z - cutStart;
        const baseOffset =
          curHeight / Math.tan(MathUtils.degToRad(this.edgeAngle ?? 0));
        x = x - baseOffset * Math.sign(x);
        y = y - baseOffset * Math.sign(y);
      }
      position.setXYZ(i, x, y, z);
    }
    position.needsUpdate = true;

    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
  }

  // private compileGroups(geometry: ExtrudeGeometry): void {
  //   //const oldGroups = geometry.groups.map((obj) => ({ ...obj }));
  //   //geometry.clearGroups();
  // }
}
